import { forwardRef, useImperativeHandle, useRef } from 'react';
import useKeyHandler from '../../../../../hooks/useKeyHandler';
import './SearchList.css';

/**
 * Renders search results with loading / empty / no-results states.
 * Pure painter — no engine calls, all actions via onRowClick.
 *
 * Keyboard navigation via useKeyHandler (same pattern as YourNotesNode):
 *   W / ArrowUp   — previous row
 *   S / ArrowDown — next row
 *   A / Escape    — back to search input (onBackToSearch)
 *   D / Enter     — select focused row (onRowClick)
 *
 * Exposes focusFirst() via ref — called by SearchDrawer on ArrowDown from search input.
 *
 * @param {object[]} results
 * @param {string}   query
 * @param {boolean}  loading
 * @param {object}   selectedEntry
 * @param {function} onRowClick       - (item) => void
 * @param {function} onBackToSearch   - () => void — focus back to search input
 */
const SearchList = forwardRef(function SearchList({ results = [], query = '', loading, selectedEntry, onRowClick, onBackToSearch }, ref) {
    const listRef = useRef(null);

    useImperativeHandle(ref, () => ({
        focusFirst() {
            const first = listRef.current?.querySelector('.sd-row');
            first?.focus();
        }
    }));

    // WASD navigation — blocked automatically when focus is on input/select/textarea
    useKeyHandler({
        onKey: ({ isUp, isDown, isBack }) => {
            const rows = Array.from(listRef.current?.querySelectorAll('.sd-row') ?? []);
            if (!rows.length) return;

            const focused = document.activeElement;
            const idx = rows.indexOf(focused);

            if (isBack) {
                onBackToSearch?.();
                return;
            }

            if (isUp) {
                if (idx > 0) rows[idx - 1].focus();
                else onBackToSearch?.(); // W on first row → back to search
                return;
            }

            if (isDown) {
                if (idx < rows.length - 1) rows[idx + 1].focus();
                return;
            }
        },
        deps: [results, onBackToSearch],
    });

    if (loading) return <div className="sd-empty">Searching…</div>;
    if (query.length < 3) return <div className="sd-empty">Type at least 3 characters</div>;
    if (results.length === 0) return <div className="sd-empty">No results for "{query}"</div>;

    return (
        <div ref={listRef}>
            {results.map((item) => {
                const selected = selectedEntry?.rk === item.rk;
                const cls = [
                    'sd-row',
                    item.stateId === 1 ? 'sd-row--completed' : '',
                    selected ? 'sd-row--selected' : '',
                ].filter(Boolean).join(' ');

                return (
                    <div
                        key={item.rk}
                        className={cls}
                        tabIndex={0}
                        onClick={() => onRowClick(item)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === 'd' || e.key === 'D') {
                                e.preventDefault();
                                onRowClick(item);
                            }
                        }}
                    >
                        <span className="sd-row-title">{item.title}</span>
                        <span className="sd-row-service">{item.service}</span>
                    </div>
                );
            })}
        </div>
    );
});

export default SearchList;
